// Global Error Handler Mixin
export const globalErrorHandler = {
  errorCaptured(err, instance, info) {
    // Log the error
    console.error('Error captured by global handler:', err, info);
    
    // Store error in Vuex store
    if (this.$store) {
      this.$store.dispatch('error/captureError', { error: err, errorInfo: info });
    }
    
    // Navigate to global error page with error details
    this.$router.push({
      name: 'ErrorBoundary',
      query: {
        timestamp: Date.now()
      }
    }).catch(() => {
      // If routing fails, at least show an alert
      this.showFallbackError();
    });
    
    // Return false to stop the error from propagating further
    return false;
  },
  
  methods: {
    showFallbackError() {
      // Fallback error display when routing fails
      if (this.$swal) {
        this.$swal({
          title: 'Oops! Something went wrong',
          text: 'It\'s our fault, not yours. Please try refreshing the page.',
          icon: 'error',
          confirmButtonText: 'Reload Page',
          allowOutsideClick: false
        }).then(() => {
          window.location.reload();
        });
      } else {
        alert('An unexpected error occurred. Please refresh the page.');
        window.location.reload();
      }
    }
  }
};

// Global error handler for unhandled promise rejections
export function setupGlobalErrorHandlers(app, router, store) {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    
    // Store error in Vuex store
    if (store) {
      store.dispatch('error/captureError', { 
        error: event.reason, 
        errorInfo: 'Unhandled Promise Rejection' 
      });
    }
    
    // Navigate to error page
    router.push({
      name: 'ErrorBoundary',
      query: {
        timestamp: Date.now()
      }
    }).catch(() => {
      // Fallback if routing fails
      showFallbackError();
    });
    
    // Prevent the default browser console error
    event.preventDefault();
  });

  // Handle global JavaScript errors
  window.addEventListener('error', (event) => {
    console.error('Global JavaScript error:', event.error);
    
    // Store error in Vuex store
    if (store) {
      store.dispatch('error/captureError', { 
        error: event.error, 
        errorInfo: `Global Error: ${event.filename}:${event.lineno}:${event.colno}` 
      });
    }
    
    // Navigate to error page
    router.push({
      name: 'ErrorBoundary',
      query: {
        timestamp: Date.now()
      }
    }).catch(() => {
      // Fallback if routing fails
      showFallbackError();
    });
    
    // Prevent the default browser console error
    event.preventDefault();
  });

  // Handle network errors and fetch failures
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    return originalFetch.apply(this, args).catch(error => {
      console.error('Fetch error:', error);
      
      // For network errors, we might not want to redirect immediately
      // Instead, let the component handle it, but log it
      if (store) {
        store.dispatch('error/captureError', { 
          error: error, 
          errorInfo: 'Network/Fetch Error' 
        });
      }
      
      // Re-throw the error so components can handle it
      throw error;
    });
  };
}

// Fallback error display function
function showFallbackError() {
  // Create a styled modal that matches the app's design
  const errorDiv = document.createElement('div');
  errorDiv.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.7);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      backdrop-filter: blur(4px);
    ">
      <div style="
        background: white;
        padding: 3rem 2.5rem;
        border-radius: 12px;
        text-align: center;
        max-width: 450px;
        margin: 1rem;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        border: 1px solid #e5e7eb;
      ">
        <div style="
          width: 64px;
          height: 64px;
          background: #fef2f2;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        ">
          <svg style="width: 32px; height: 32px; color: #dc2626;" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM13 17h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
        </div>
        
        <h2 style="
          color: #1f2937;
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          line-height: 1.4;
        ">Oops! Something went wrong</h2>
        
        <p style="
          color: #6b7280;
          font-size: 1rem;
          margin-bottom: 2rem;
          line-height: 1.5;
        ">It's our fault, not yours. Please try refreshing the page.</p>
        
        <button onclick="window.location.reload()" style="
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          border: none;
          padding: 0.875rem 2rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 500;
          transition: all 0.2s ease;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          min-width: 140px;
        " onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 6px 12px -2px rgba(0, 0, 0, 0.15)'" 
           onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 6px -1px rgba(0, 0, 0, 0.1)'">
          <svg style="width: 16px; height: 16px; margin-right: 8px; display: inline-block; vertical-align: middle;" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
          </svg>
          Reload Page
        </button>
        
        <div style="
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e5e7eb;
        ">
          <p style="
            color: #9ca3af;
            font-size: 0.875rem;
            margin: 0;
          ">© Wiremit</p>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(errorDiv);
}

// Enhanced error boundary component factory
export function createErrorBoundary(fallbackComponent = 'ErrorBoundary') {
  return {
    name: 'ErrorBoundaryWrapper',
    data() {
      return {
        hasError: false,
        error: null,
        errorInfo: null
      };
    },
    errorCaptured(err, instance, info) {
      this.hasError = true;
      this.error = err;
      this.errorInfo = info;
      console.error('Error caught by boundary wrapper:', err, info);
      
      // Store error in Vuex if available
      if (this.$store) {
        this.$store.dispatch('error/captureError', { error: err, errorInfo: info });
      }
      
      return false; // Stop error propagation
    },
    render(h) {
      if (this.hasError) {
        // Render the fallback component
        return h(fallbackComponent, {
          props: {
            error: this.error,
            errorInfo: this.errorInfo
          }
        });
      }
      
      // Render children normally
      return this.$slots.default;
    }
  };
}

// Utility function to handle async errors in components
export function handleAsyncError(error, vm, info = '') {
  console.error('Async error:', error, info);
  
  if (vm && vm.$store) {
    vm.$store.dispatch('error/captureError', { error, errorInfo: info });
  }
  
  if (vm && vm.$router) {
    vm.$router.push({
      name: 'ErrorBoundary',
      query: {
        timestamp: Date.now()
      }
    }).catch(() => {
      showFallbackError();
    });
  }
}
